import type { IHttpClient } from '@wix/yoshi-flow-editor';
import type { Post } from '@wix/ambassador-blog-v3-post-http/types';
import type { ErrorHandlerPublicAPI } from '@wix/yoshi-flow-editor/error-handler';
import { appendAuthHeaders } from '../controller/helpers';
import { type PlatformApi } from '../controller/platform-api';
import getQuery from './get-query';
import { toWixCodePost } from './platformized-post-mapper';
import urlService from './url';

export const fieldsToIncludeQuery = [
  'CONTENT_TEXT',
  'RICH_CONTENT',
  'URL',
  'METRICS',
  'INTERNAL_ID',
]
  .map((field) => `fieldsets=${field}`)
  .join('&');

type PlatformizedPostRequestArgs = {
  instance: string;
  platformApi: PlatformApi;
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
};

const fetchPlatformized = async <T>(
  { instance, platformApi, httpClient, errorHandler }: PlatformizedPostRequestArgs,
  path: string,
) => {
  const { withErrorHandler } = errorHandler;
  const { data } = await withErrorHandler(
    () =>
      httpClient.get(`${urlService.getAPIUrl(platformApi)}${path}`, {
        headers: appendAuthHeaders(platformApi, { instance }),
      }),
    { errorCodesMap: { applicationError: { UNAUTHENTICATED: false } } },
  );
  return data as T;
};

const getPlatformizedPostBySlug = async ({
  slug,
  ...args
}: PlatformizedPostRequestArgs & { slug: string }) => {
  try {
    const response = await fetchPlatformized<{ post: Post }>(
      args,
      `/posts/slugs/${slug}?${fieldsToIncludeQuery}`,
    );
    return toWixCodePost(response.post);
  } catch (err) {
    return;
  }
};

const getPlatformizedPostById = async ({
  id,
  ...args
}: PlatformizedPostRequestArgs & { id: string }) => {
  try {
    const response = await fetchPlatformized<{ post: Post }>(
      args,
      `/posts/${id}?${fieldsToIncludeQuery}`,
    );
    return toWixCodePost(response.post);
  } catch (err) {
    return;
  }
};

const getPlatformizedPosts = async ({
  params,
  ...args
}: PlatformizedPostRequestArgs & { params: { [key: string]: number | string } }) => {
  try {
    const response = await fetchPlatformized<{ posts: Post[] }>(
      args,
      `/posts?${getQuery(params)}&${fieldsToIncludeQuery}`,
    );
    return response.posts ? response.posts.map(toWixCodePost) : [{}];
  } catch (err) {
    return [];
  }
};
const getPlatformizedLastPost = (args: PlatformizedPostRequestArgs) =>
  getPlatformizedPosts({
    ...args,
    params: { 'paging.limit': 1 },
  }).then((posts) => posts[0]);

export default {
  getPlatformizedPostBySlug,
  getPlatformizedLastPost,
  getPlatformizedPostById,
};
